import { EvidenceType } from '../types/emissions';

export const EVIDENCE_TYPE_LABEL: Record<EvidenceType, string> = {
  [EvidenceType.Invoice]: 'Invoice',
  [EvidenceType.MeterReading]: 'Meter reading',
  [EvidenceType.UtilityBill]: 'Utility bill',
  [EvidenceType.DeliveryNote]: 'Delivery note',
  [EvidenceType.Certificate]: 'Certificate',
  [EvidenceType.AuditReport]: 'Audit report',
  [EvidenceType.ContractOrPPA]: 'Contract / PPA',
  [EvidenceType.SupplierAttestation]: 'Supplier attestation',
  [EvidenceType.CalculationWorkbook]: 'Calculation workbook',
  [EvidenceType.MethodologyDoc]: 'Methodology doc',
  [EvidenceType.Photo]: 'Photo',
  [EvidenceType.Other]: 'Other',
};

export const EVIDENCE_TYPE_OPTIONS: ReadonlyArray<{ value: EvidenceType; label: string }> =
  (Object.entries(EVIDENCE_TYPE_LABEL) as [EvidenceType, string][]).map(([value, label]) => ({
    value,
    label,
  }));

export type EvidenceTint = 'rose' | 'blue' | 'amber' | 'teal' | 'violet' | 'slate';

export const EVIDENCE_TYPE_TINT: Record<EvidenceType, EvidenceTint> = {
  [EvidenceType.Invoice]: 'rose',
  [EvidenceType.MeterReading]: 'blue',
  [EvidenceType.UtilityBill]: 'amber',
  [EvidenceType.DeliveryNote]: 'slate',
  [EvidenceType.Certificate]: 'teal',
  [EvidenceType.AuditReport]: 'violet',
  [EvidenceType.ContractOrPPA]: 'violet',
  [EvidenceType.SupplierAttestation]: 'slate',
  [EvidenceType.CalculationWorkbook]: 'teal',
  [EvidenceType.MethodologyDoc]: 'violet',
  [EvidenceType.Photo]: 'blue',
  [EvidenceType.Other]: 'slate',
};

// Full literal class strings so Tailwind's scanner picks them up — no
// interpolation. Keep in sync with EvidenceTint above.
export const TINT_CLASSES: Record<EvidenceTint, { bg: string; ink: string }> = {
  rose: { bg: 'tw:bg-rose-50', ink: 'tw:text-rose-700' },
  blue: { bg: 'tw:bg-blue-50', ink: 'tw:text-blue-700' },
  amber: { bg: 'tw:bg-amber-50', ink: 'tw:text-amber-700' },
  teal: { bg: 'tw:bg-teal-50', ink: 'tw:text-teal-700' },
  violet: { bg: 'tw:bg-violet-50', ink: 'tw:text-violet-700' },
  slate: { bg: 'tw:bg-slate-100', ink: 'tw:text-slate-700' },
};

export interface MimeThumb {
  ext: string;
  tint: EvidenceTint;
  icon: string;
}

// Single registry per mime — adding a format means one row, not edits in
// three parallel maps.
const MIME_REGISTRY: Record<string, MimeThumb> = {
  'application/pdf': { ext: 'PDF', tint: 'rose', icon: 'fa-file-pdf' },
  'image/png': { ext: 'PNG', tint: 'blue', icon: 'fa-file-image' },
  'image/jpeg': { ext: 'JPG', tint: 'blue', icon: 'fa-file-image' },
  'image/webp': { ext: 'WEBP', tint: 'blue', icon: 'fa-file-image' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { ext: 'XLSX', tint: 'teal', icon: 'fa-file-spreadsheet' },
  'application/vnd.ms-excel': { ext: 'XLS', tint: 'teal', icon: 'fa-file-spreadsheet' },
  'text/csv': { ext: 'CSV', tint: 'teal', icon: 'fa-file-spreadsheet' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { ext: 'DOCX', tint: 'violet', icon: 'fa-file-lines' },
};

const FALLBACK_IMAGE_ICON = 'fa-file-image';
const GENERIC_ICON = 'fa-file';

export function getMimeThumb(mimeType: string, fileName: string): MimeThumb {
  const entry = MIME_REGISTRY[mimeType];
  if (entry) {
    return entry;
  }
  // Cover the long tail of image formats not pinned to a registry row.
  const icon = mimeType.startsWith('image/') ? FALLBACK_IMAGE_ICON : GENERIC_ICON;
  const ext = fileName.includes('.') ? fileName.slice(fileName.lastIndexOf('.') + 1).toUpperCase() : 'FILE';
  return { ext: ext.slice(0, 5), tint: 'slate', icon };
}

export function getMimeIcon(mimeType: string): string {
  return MIME_REGISTRY[mimeType]?.icon
    ?? (mimeType.startsWith('image/') ? FALLBACK_IMAGE_ICON : GENERIC_ICON);
}
